import React from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { Quote, MessageCircle, ArrowRight, Sparkles, PenLine } from "lucide-react";
import { colors, communityStories, externalLinks } from "../config/site";

const SUBMIT_STORY_URL = externalLinks.storySubmissionForm;
const SUBMIT_STORY_INTERNAL = "/contact?topic=story";

const SubmitStoryButton = ({ testId = "submit-your-story-btn", className = "" }) => {
  const buttonEl = (
    <Button
      data-testid={testId}
      className={`rounded-full px-5 py-5 font-semibold ${className}`}
      style={{ backgroundColor: colors.terracotta, color: colors.white }}
    >
      <PenLine className="mr-2 w-4 h-4" />
      Submit Your Story
      <ArrowRight className="ml-2 w-4 h-4" />
    </Button>
  );

  if (SUBMIT_STORY_URL) {
    return (
      <a href={SUBMIT_STORY_URL} target="_blank" rel="noopener noreferrer">
        {buttonEl}
      </a>
    );
  }
  return <Link to={SUBMIT_STORY_INTERNAL}>{buttonEl}</Link>;
};

const StoryCard = ({ story }) => (
  <div
    className="rounded-3xl p-7 shadow-sm hover:shadow-md transition-all flex flex-col h-full"
    style={{ backgroundColor: colors.white, borderTop: `3px solid ${colors.terracotta}` }}
  >
    <Quote
      className="w-8 h-8 mb-4"
      style={{ color: colors.terracotta }}
      strokeWidth={2.5}
    />
    <p
      className="text-base lg:text-lg leading-relaxed mb-6 flex-grow"
      style={{ color: colors.charcoal }}
    >
      &ldquo;{story.quote}&rdquo;
    </p>
    <div className="flex items-center gap-3 pt-4 border-t" style={{ borderColor: "#EDE7DA" }}>
      {story.photo ? (
        <img
          src={story.photo}
          alt={story.name}
          className="w-11 h-11 rounded-full object-cover"
        />
      ) : (
        <div
          className="w-11 h-11 rounded-full flex items-center justify-center font-bold text-white"
          style={{ backgroundColor: colors.sage }}
        >
          {story.name.charAt(0)}
        </div>
      )}
      <div>
        <p className="font-bold text-sm" style={{ color: colors.charcoal }}>
          {story.name}
        </p>
        <p className="text-xs" style={{ color: colors.textMuted }}>
          {story.role}
          {story.location ? ` · ${story.location}` : ""}
        </p>
      </div>
    </div>
  </div>
);

const InvitationCard = () => (
  <div
    className="rounded-3xl p-7 shadow-sm hover:shadow-md transition-all flex flex-col h-full text-white"
    style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})` }}
    data-testid="stories-share-invitation"
  >
    <MessageCircle className="w-9 h-9 mb-4" style={{ color: colors.gold }} />
    <h3 className="text-xl font-bold mb-3">Share your story</h3>
    <p className="text-sm opacity-90 mb-6 flex-grow">
      Are you a program participant, partner organization, or community member with a Dorsey Taylor
      Homes experience to share? Your voice helps other families see what&apos;s possible.
    </p>
    <SubmitStoryButton testId="stories-share-cta" className="w-full sm:w-auto" />
  </div>
);

const EmptyState = () => (
  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
    {/* Two waiting cards */}
    {[1, 2].map((i) => (
      <div
        key={i}
        className="rounded-3xl p-7 shadow-sm flex flex-col items-start justify-center h-full"
        style={{
          backgroundColor: colors.white,
          border: `2px dashed ${colors.gold}`,
          minHeight: "260px",
        }}
        data-testid={`stories-placeholder-${i}`}
      >
        <div
          className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4"
          style={{ backgroundColor: colors.cream, color: colors.terracottaDark }}
        >
          <Sparkles className="w-6 h-6" />
        </div>
        <p
          className="text-xs uppercase tracking-widest font-semibold mb-2"
          style={{ color: colors.gold }}
        >
          Story Coming Soon
        </p>
        <p className="text-sm" style={{ color: colors.textMuted }}>
          A space reserved for the voices of program participants, partners, and community members
          we&apos;re honored to serve.
        </p>
      </div>
    ))}
    <InvitationCard />
  </div>
);

const Stories = () => {
  const hasStories = communityStories.length > 0;

  return (
    <section
      className="py-20 px-4"
      style={{ backgroundColor: colors.cream }}
      data-testid="community-stories-section"
    >
      <div className="container mx-auto max-w-7xl">
        <div className="text-center mb-12 max-w-3xl mx-auto">
          <p
            className="text-xs uppercase tracking-widest font-semibold mb-3"
            style={{ color: colors.terracotta }}
          >
            Stories from the Community
          </p>
          <h2
            className="text-3xl lg:text-4xl font-bold mb-4"
            style={{ color: colors.charcoal }}
          >
            Voices that shape the work.
          </h2>
          <p className="text-base lg:text-lg" style={{ color: colors.textMuted }}>
            The families we house, the partners we work alongside, and the community members we serve
            are at the center of everything we do. Their stories will live here.
          </p>
          <div className="mt-7 flex justify-center">
            <SubmitStoryButton testId="stories-header-cta" />
          </div>
        </div>

        {hasStories ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {communityStories.slice(0, 2).map((story, idx) => (
              <StoryCard key={idx} story={story} />
            ))}
            <InvitationCard />
          </div>
        ) : (
          <EmptyState />
        )}
      </div>
    </section>
  );
};

export default Stories;
